// #758 -- the export dialog's settings and the arithmetic behind them
// (docs/model/drawing-ui-export.md). Pure: no DOM, so it is unit-tested directly.

export type ExportFormat = 'svg' | 'png' | 'jpeg' | 'pdf';
// #772 -- Page is exactly the drawing's page, clipping anything beyond it; Whole drawing and
// Selection crop to the artwork plus padding. #1140 (owner): Whole drawing is the default -- an
// export pasted into a document is the drawing, not a sheet of mostly empty paper.
export type ExportScope = 'page' | 'whole' | 'selection';
export type LengthUnit = 'mm' | 'in';
export type ExportBackground = { readonly kind: 'transparent' } | { readonly kind: 'color'; readonly color: string };

export interface ExportSettings {
  readonly format: ExportFormat;
  readonly scope: ExportScope;
  readonly background: ExportBackground;
  readonly paddingMm: number;
  // The output size; null means "natural" (the artwork's own size plus padding).
  readonly sizeMm: { readonly width: number; readonly height: number } | null;
  readonly lockProportions: boolean;
  readonly unit: LengthUnit;
  readonly dpi: number;
}

export const MM_PER_INCH = 25.4;
export const DEFAULT_PADDING_MM = 2;
export const DEFAULT_DPI = 300;
export const DPI_PRESETS = [300, 600] as const;

export const WHITE: ExportBackground = { kind: 'color', color: '#ffffff' };
export const TRANSPARENT: ExportBackground = { kind: 'transparent' };

// Accepted defaults: PNG/SVG transparent; JPEG/PDF white. JPEG cannot be transparent.
export function defaultBackground(format: ExportFormat): ExportBackground {
  return format === 'png' || format === 'svg' ? TRANSPARENT : WHITE;
}
export const supportsTransparency = (format: ExportFormat) => format !== 'jpeg' && format !== 'pdf';
export const isRaster = (format: ExportFormat) => format === 'png' || format === 'jpeg';

// The unit defaults to the drawing page's own unit (#772: Letter is inches, A4 millimetres).
export function defaultSettings(format: ExportFormat = 'svg', unit: LengthUnit = 'mm'): ExportSettings {
  return {
    format,
    scope: 'whole',
    background: defaultBackground(format),
    paddingMm: DEFAULT_PADDING_MM,
    sizeMm: null,
    lockProportions: true,
    unit,
    dpi: DEFAULT_DPI
  };
}

// Switching format keeps the user's choices but swaps a background the new format can't use (or
// one that was only the old format's default) for the new default.
export function withFormat(settings: ExportSettings, format: ExportFormat): ExportSettings {
  const wasDefault = JSON.stringify(settings.background) === JSON.stringify(defaultBackground(settings.format));
  const unusable = settings.background.kind === 'transparent' && !supportsTransparency(format);
  return { ...settings, format, background: wasDefault || unusable ? defaultBackground(format) : settings.background };
}

export const toUnit = (mm: number, unit: LengthUnit) => (unit === 'mm' ? mm : mm / MM_PER_INCH);
export const fromUnit = (value: number, unit: LengthUnit) => (unit === 'mm' ? value : value * MM_PER_INCH);

// Resizing with proportions locked scales the other side to keep the artwork's aspect ratio.
export function resize(
  natural: { readonly width: number; readonly height: number },
  current: { readonly width: number; readonly height: number },
  side: 'width' | 'height',
  valueMm: number,
  lock: boolean
): { width: number; height: number } {
  if (!lock) return { ...current, [side]: valueMm };
  const ratio = natural.height / natural.width;
  return side === 'width' ? { width: valueMm, height: valueMm * ratio } : { width: valueMm / ratio, height: valueMm };
}

export function pixelSize(sizeMm: { readonly width: number; readonly height: number }, dpi: number) {
  return {
    width: Math.max(1, Math.round((sizeMm.width / MM_PER_INCH) * dpi)),
    height: Math.max(1, Math.round((sizeMm.height / MM_PER_INCH) * dpi))
  };
}

// Browsers refuse canvases past roughly these limits (Safari is the tightest).
export const MAX_CANVAS_SIDE = 16384;
export const MAX_CANVAS_AREA = 16384 * 16384 * 0.25;

export type ExportBlocker = 'emptySelection' | 'emptyDrawing' | 'invalidSize' | 'tooManyPixels' | 'invalidDpi';

export function exportBlockers(
  settings: ExportSettings,
  sizeMm: { readonly width: number; readonly height: number },
  content: { readonly selectionEmpty: boolean; readonly drawingEmpty: boolean }
): ExportBlocker[] {
  const blockers: ExportBlocker[] = [];
  if (settings.scope === 'selection' && content.selectionEmpty) blockers.push('emptySelection');
  if (settings.scope === 'whole' && content.drawingEmpty) blockers.push('emptyDrawing');
  if (!(sizeMm.width > 0 && sizeMm.height > 0) || !Number.isFinite(sizeMm.width + sizeMm.height)) {
    blockers.push('invalidSize');
  } else if (isRaster(settings.format)) {
    if (!(settings.dpi >= 36 && settings.dpi <= 2400)) blockers.push('invalidDpi');
    else {
      const pixels = pixelSize(sizeMm, settings.dpi);
      if (
        pixels.width > MAX_CANVAS_SIDE ||
        pixels.height > MAX_CANVAS_SIDE ||
        pixels.width * pixels.height > MAX_CANVAS_AREA
      )
        blockers.push('tooManyPixels');
    }
  }
  return blockers;
}

export const FILE_EXTENSION: Record<ExportFormat, string> = { svg: 'svg', png: 'png', jpeg: 'jpg', pdf: 'pdf' };
export const MEDIA_TYPE: Record<ExportFormat, string> = {
  svg: 'image/svg+xml',
  png: 'image/png',
  jpeg: 'image/jpeg',
  pdf: 'application/pdf'
};
